import type { CharacterCommission, JournalEntry } from '@sorrem/shared';

/** A Journal date, as the entries are dated: "Oct 10, 2026". */
export function formatJournalDate(at: string): string {
    return new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The entry's title, as it heads its page. */
export function formatJournalEntryTitle(entry: JournalEntry): string {
    return entry.kind === 'cutscene' ? entry.cutscene.title : entry.content.title;
}

/** URL of the ink sketch drawn beside the entry, when it has one. */
export function resolveJournalEntrySketch(entry: JournalEntry): string | undefined {
    return entry.kind === 'cutscene' ? entry.cutscene.sketchUrl : entry.content.journalSketch;
}

/** Where a commission stands, in a sentence: "Accepted Oct 10, 2026. In progress." */
export function describeCommissionStatus(commission: CharacterCommission): string {
    const on = (at: string | null): string => (at !== null ? ` ${formatJournalDate(at)}` : '');
    switch (commission.status) {
        case 'completed':
            return `Completed${on(commission.completedAt)}.`;
        case 'accepted':
            return `Accepted${on(commission.acceptedAt)}. In progress.`;
        case 'offered':
            return `Offered${on(commission.offeredAt)}. Not yet accepted.`;
    }
}

/** The selected entry, falling back to the first when none (or a stale one) is selected. */
export function resolveSelectedJournalEntry(entries: readonly JournalEntry[], selectedId: string | null): JournalEntry | null {
    return entries.find((entry) => entry.id === selectedId) ?? entries[0] ?? null;
}

/** The route that replays a watched cutscene from the Journal. */
export function toJournalReplayPath(cutsceneId: string): string {
    return `/character/journey/cutscenes/${encodeURIComponent(cutsceneId)}`;
}
